import { useEffect, useState } from "react";
import { Button, Checkbox, Grid, IconButton, TextField, Tooltip, Typography } from "@mui/material";
import DownloadIcon from '@mui/icons-material/Download';
import EditIcon from '@mui/icons-material/Edit';
import DeleteIcon from '@mui/icons-material/Delete';

import { planColumns } from "./grid-columns";
import { Plan } from "../../../../../models/plan";
import { planFormSchema } from "./plan-form.schema";
import { DeletePlanDialog } from "./delete-plan.dialog";
import { useSaveData } from "../../../../../hooks/useSaveData";
import { useDeleteData } from "../../../../../hooks/useDeleteData";
import { UploadPlanDialog } from "./upload-plan/upload-plan.dialog";
import { planApiKeyStore } from "../../../../../api/plan-api-key-store";
import { useFileDownload } from "../../../../../components/file-download";
import { GuardedComponent } from "../../../../../components/guarded-component";
import { UserPermissionName } from "../../../../../models/user-permission-name";
import { fileUploadApiKeyStore } from "../../../../../api/file-upload-key-store";
import { SiTrackGrid } from "../../../../../components/si-track-grid/si-track-grid";
import { planReviewApiKeyStore } from "../../../../../api/plan-review-api-key-store";
import { useAuthorize } from "../../../../../hooks/use-authorize";
import { useLoadData } from "../../../../../hooks/useLoadData";
import { ApiResponse } from "../../../../../models/api-response";
import { GridColumnConfig, GridPreference } from "../../../../../models/gridCoumnConfig";
import { gridApiKeyStore } from "../../../../../api/grid-api-key-store";
import { State } from "@progress/kendo-data-query";
import useDebounce from "../../../../../hooks/use-debounce";
import { useSnackbar } from "../../../../../hooks/use-snackbar.hook";

interface CellProps {
    moduleData: any;
}

interface CustomGridCellProps {
    field: string;
}

interface PlanDetailsComponent {
    title: string;
    planReviewTaskData: any;
    fileOrder: any;
    isLoading: boolean;
    selectedPlansData: (plans: any) => void;
}

const downloadBlob = (blob: Blob, fileName: string) => {
    const url = window.URL.createObjectURL(blob);
    const link = document.createElement('a');
    link.href = url;
    link.download = fileName;

    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
    window.URL.revokeObjectURL(url);
};

const handleFileFunctionality = (fileResponse: any, fileData: any) => {
    if (fileResponse) {
        if (typeof fileResponse === 'string') {
            const blob = new Blob([new Uint8Array(
                Array.from(atob(fileResponse), (char) => char.charCodeAt(0)))],
                { type: fileData?.fileType });
            downloadBlob(blob, fileData?.fileName ?? 'file');
        } else {
            throw new Error('Invalid data type for file download.');
        }
    }
}

export const PlanDetailsComponent = ({ title, planReviewTaskData, fileOrder, selectedPlansData, isLoading }: PlanDetailsComponent) => {

    const isAddPlan = useAuthorize([UserPermissionName?.ADD_PLAN]);
    const isDownloadFile = useAuthorize([UserPermissionName?.DOWNLOAD_FILE]);

    const { showSnackbar } = useSnackbar();

    const [planID, setPlanID] = useState<any>();
    const [editItem, setEditItem] = useState<Partial<Plan>>();
    const [updatedPlanData, setPlanData] = useState<any[]>();
    const [selectedPlanData, setSelectPlanData] = useState<any[]>([]);
    const [isConfirmDelete, setConfirmDialog] = useState<boolean>(false);

    const { isLoading: isFilesLoading, mutateAsync: mutateFileAsync } = useSaveData<any>(fileUploadApiKeyStore.downloadFile());

    // const { isLoading: isDeleteLoading, mutateAsync: mutateDeletePlanAsync } = useDeleteData(planApiKeyStore.deletePlan(planID),
    //     () => planReviewApiKeyStore.getPlanReviewTask(planReviewTaskData?.planReviewTaskID));

    const { isLoading: isDeleteLoading, mutateAsync: mutateDeletePlanAsync } = useSaveData<any>(planApiKeyStore.removeMapPlan(),
        () => planReviewApiKeyStore.getPlanReviewTask(planReviewTaskData?.planReviewTaskID));

    const { data: mapPlanData, isLoading: isMapPlanLoading, isFetching: isMapPlanFetching, refetch } = useLoadData<ApiResponse<any[]>>(
        planApiKeyStore.getPlans({
            // officeID: planRequestArgs.officeID,
            // projectID: planRequestArgs.projectID,
            unitID: planReviewTaskData?.unitID,
        }), !!planReviewTaskData?.unitID
    );

    const { downloadFile, isLoading: isFileDownloadLoading } = useFileDownload();

    useEffect(() => {
        if (planReviewTaskData) {
            const updatedData = planReviewTaskData?.unitPlansList?.map((pl: any) => ({
                ...pl,
                isSelected: pl?.isFileMapped,
            }));
            setPlanData(updatedData);
            setSelectPlanData(updatedData?.filter((pla: any) => pla?.isSelected == true) ?? [])
            selectedPlansData(updatedData?.filter((pla: any) => pla?.isSelected == true) ?? [])
        }
    }, [planReviewTaskData, isLoading]);



    const handleCreatePlan = () => {
        setEditItem({
            customerID: planReviewTaskData?.customerID,
            officeID: planReviewTaskData?.officeID,
            projectID: planReviewTaskData?.projectID,
            unitID: planReviewTaskData?.unitID
        });
    };

    const handleDownloadFiles = async () => {
        for (const planData of selectedPlanData) {
            let fileData;
            if (planData?.itemType == "Plan") {
                fileData = {
                    planID: planData?.planID,
                    fileName: planData?.fileName,
                    fileType: planData?.fileType,
                    fileSection: "string",
                };
            }
            else {
                fileData = {
                    fileID: planData?.fileID,
                    fileName: planData?.fileName,
                    fileType: planData?.fileType,
                    fileSection: "string",
                };
            }
            const fileResponse = await mutateFileAsync({ data: fileData });
            handleFileFunctionality(fileResponse, fileData);
        }
    }

    const handleDownloadFile = async (data: any) => {
        const value = {
            planID: Number(data?.planID) || 0,
            fileName: data?.fileName,
            fileType: data?.fileType,
        };
        downloadFile({ additionalData: value });
    };

    const handleEditPlan = (planData: any) => {
        setEditItem({
            ...planData,
            customerID: planReviewTaskData?.customerID,
            officeID: planReviewTaskData?.officeID,
            projectID: planReviewTaskData?.projectID,
            unitID: planReviewTaskData?.unitID,
        });
    }

    const handleEditClose = () => {
        setEditItem(undefined);
    };

    const handleSavePlan = () => {
        refetch();
        setEditItem(undefined);
    }

    const openConfirmDialog = (id: any) => {
        setPlanID(id);
        setConfirmDialog(true);
    };

    const handleDeletePlan = async () => {
        const deletePlan = {
            planID: planID,
            unitID: planReviewTaskData?.unitID
        }
        await mutateDeletePlanAsync({ data: deletePlan });
        refetch();
        setConfirmDialog(false);
    };

    const SelectPlanCell = ({ moduleData }: CellProps) => {
        const handleChange = (event: React.ChangeEvent<HTMLInputElement>) => {
            const updatedData = updatedPlanData?.map((d: any) => ({
                ...d,
                isSelected: (moduleData?.planID ?
                    moduleData?.planID == d?.planID : moduleData?.fileID == d?.fileID) ? event.target.checked : d?.isSelected,
                fileOrder: (moduleData?.planID ?
                    moduleData?.planID == d?.planID : moduleData?.fileID == d?.fileID) ?
                    planReviewTaskData?.unitPlansList?.find((f: any) => moduleData?.planID ?
                        moduleData?.planID == f?.planID : moduleData?.fileID == f?.fileID)?.fileOrder ?? 0
                    : d?.fileOrder,
            }));
            setPlanData(updatedData);
            setSelectPlanData(updatedData?.filter((pla: any) => pla?.isSelected == true) ?? [])
            selectedPlansData(updatedData?.filter((pla: any) => pla?.isSelected == true) ?? [])
        };

        return (
            <td>
                <Checkbox
                    checked={updatedPlanData?.find((pl: any) => (moduleData?.planID ?
                        pl.planID == moduleData?.planID : pl.fileID == moduleData?.fileID))?.isSelected}
                    onChange={handleChange}
                    inputProps={{ 'aria-label': 'controlled' }}
                    disabled={!moduleData?.fileURL}
                />
            </td>
        );
    };

    const FileDownloadCell = ({ moduleData }: CellProps) => {
        const fileURL = moduleData?.fileURL;

        return (
            <td>
                {fileURL ? (
                    <GuardedComponent requiredPermissions={[UserPermissionName.DOWNLOAD_FILE]}>
                        <IconButton style={{ padding: 5 }} aria-label="view" onClick={() => handleDownloadFile(moduleData)}>
                            <Tooltip title={<Typography variant="body1">Download</Typography>}>
                                <DownloadIcon className="btnEdit" />
                            </Tooltip>
                        </IconButton>
                    </GuardedComponent>
                ) : (<Typography variant="body1">No file uploaded</Typography>)
                }
            </td>
        );
    };

    const checkFileOrderValue = (planData: any, newValue: any) => {
        const isFileOrderUsed = planData?.some(
            (plan: any) =>
                plan?.fileOrder !== null && Number(plan.fileOrder) === Number(newValue.fileOrder) && plan.id !== newValue.planID
        );
        return isFileOrderUsed
    }

    const handleOrderChange = (moduleData: any, fileValue: any) => {
        if (fileOrder?.includes(Number(fileValue))) {
            showSnackbar(`This file order has already been assigned to an uploaded file.`, 'error');
            return;
        }

        if (checkFileOrderValue(updatedPlanData, { planID: moduleData?.id, fileOrder: fileValue })) {
            showSnackbar(`This file order has already been assigned to the another plan.`, 'error');
            return;
        }

        const updatedData = updatedPlanData?.map((d: any) => ({
            ...d,
            fileOrder: (moduleData?.planID ?
                moduleData?.planID == d?.planID : moduleData?.fileID == d?.fileID) ? fileValue : d?.fileOrder,
        }));
        setPlanData(updatedData);
        setSelectPlanData(updatedData?.filter((pla: any) => pla?.isSelected == true) ?? [])
        selectedPlansData(updatedData?.filter((pla: any) => pla?.isSelected == true) ?? [])
    }

    const FileOrderCell = ({ moduleData }: CellProps) => {
        const isPlanSelected = moduleData?.isSelected;
        const [fileOrderValue, setFileOrderValue] = useState<any>(moduleData?.fileOrder);

        const handleChange = (event: any) => {
            setFileOrderValue(event?.target?.value)
        }

        return (
            <td>
                {isPlanSelected ?
                    <TextField
                        hiddenLabel
                        type="number"
                        id="filled-hidden-label-small"
                        variant="outlined"
                        size="small"
                        value={fileOrderValue}
                        onChange={handleChange}
                        onBlur={(e) => handleOrderChange(moduleData, fileOrderValue)}
                        onInput={(e: any) => {
                            if (e.target.value < 0) e.target.value = 0;
                        }}
                        inputProps={{ min: 0 }}
                    />
                    : <Typography variant="body1">{moduleData?.fileOrder}</Typography>
                }
            </td>
        );
    };

    const CustomGridCell = ({ field }: CustomGridCellProps) => {
        if (field === 'select') {
            return (dataItem: any) => {
                return <SelectPlanCell moduleData={dataItem.dataItem} />;
            };
        }
        if (field === 'fileName') {
            return (dataItem: any) => {
                return <FileDownloadCell moduleData={dataItem.dataItem} />;
            };
        }
        if (field === 'fileOrder') {
            return (dataItem: any) => {
                return <FileOrderCell moduleData={dataItem.dataItem} />;
            };
        }
    }

    const editAndDeleteElement = (planData: any) => {
        const itemType = planData?.dataItem?.itemType
        return (
            (itemType == "Plan") && (
                <>
                    <GuardedComponent requiredPermissions={[UserPermissionName.EDIT_PLAN]}>
                        <IconButton aria-label="edit" onClick={() => handleEditPlan(planData?.dataItem)}>
                            <Tooltip title={<Typography variant="body1">Edit</Typography>}>
                                <EditIcon className="btnEdit" />
                            </Tooltip>
                        </IconButton>
                    </GuardedComponent>
                    <GuardedComponent requiredPermissions={[UserPermissionName.DELETE_PLAN]}>
                        <IconButton aria-label="delete" onClick={() => openConfirmDialog(planData?.dataItem?.planID)}>
                            <Tooltip title={<Typography variant="body1">Delete</Typography>}>
                                <DeleteIcon className="btnDelete" />
                            </Tooltip>
                        </IconButton>
                    </GuardedComponent>
                </>
            )
        );
    }

    // -------------------------------------------------Grid Layout------------------------------
    const titleName = title?.split(" ")?.join("");
    const {
        isLoading: isLoadingGridLayout,
        data: gridLayouts,
        refetch: refetchGridLayout
    } = useLoadData<ApiResponse<GridPreference>>(
        gridApiKeyStore.getGridLayout(`${titleName}PlanReviewLibrary`, `${titleName}PlanReviewLibrary`)
    );

    const { mutateAsync } = useSaveData<any>(gridApiKeyStore.saveGridLayOut());
    const [gridConfig, setGridConfig] = useState<{
        dataState: State | undefined;
        columns: any[] | undefined;
    }>();

    const [filteredPlanLibraries, setFilteredPlanLibraries] = useState<Plan[]>([]);

    const [searchTerm, setSearchTerm] = useState('');
    const debouncedSearchTerm = useDebounce(searchTerm, 300);

    useEffect(() => {
        const filteredPlanLibraryList = updatedPlanData?.filter((PlanLibrary: any) =>
            Object.values(PlanLibrary).some(
                (value) => value && value.toString().toLowerCase().includes(debouncedSearchTerm.toLowerCase())
            )
        );
        setFilteredPlanLibraries(filteredPlanLibraryList || []);
    }, [debouncedSearchTerm, updatedPlanData]);

    const handleInputChange = (value: string) => {
        setSearchTerm(value);
    };

    const handleSaveLayout = (gridDataState: State, gridColumns: GridColumnConfig[]) => {
        const data = {
            filterJSON: JSON.stringify(gridDataState),
            columnJSON: JSON.stringify(gridColumns),
            gridName: `${titleName}PlanReviewLibrary`,
            pageName: `${titleName}PlanReviewLibrary`,
        };

        mutateAsync({ data }).then(() => {
            refetchGridLayout();
        });
    };

    const handleResetLayout = () => {
        const data = {
            filterJSON: null,
            columnJSON: null,
            gridName: `${titleName}PlanReviewLibrary`,
            pageName: `${titleName}PlanReviewLibrary`,
        };

        mutateAsync({ data }).then(() => {
            refetchGridLayout();
        });
    };

    useEffect(() => {
        if (!gridLayouts?.data) return;

        try {
            const filterData = gridLayouts.data.filterJSON ? JSON.parse(gridLayouts.data.filterJSON) : null;

            const columnJson = gridLayouts.data.columnJSON ? JSON.parse(gridLayouts.data.columnJSON) : null;

            // if (filterData && columnJson) {
            setGridConfig({
                columns: columnJson,
                dataState: filterData,
            });
            // }
        } catch (error) {
            console.log('Error parsing grid layout JSON:', error);
        }
    }, [gridLayouts?.data]);

    // ----------------------------------------------------------------------------------------

    return (
        <Grid container spacing={1}>
            <Grid className="subHead" item lg={12} sm={12} xl={12} xs={12}>
                <Typography variant="h3" color="textPrimary">
                    PLANS
                </Typography>
            </Grid>
            <Grid item lg={12} sm={12} xl={12} xs={12}>
                <SiTrackGrid
                    fileTitle='plan_review_plan'
                    columns={planColumns}
                    dataItemKey="id"
                    customCell={(field) =>
                        CustomGridCell({
                            field,
                        })
                    }
                    currentPageSize={5}
                    // onGridDataChange={handleOnChange}
                    CustomComponent={editAndDeleteElement}
                    actionPermissions={{
                        export: UserPermissionName.DOWNLOAD_GRID
                    }}
                    isBusy={
                        isLoading ||
                        isFileDownloadLoading ||
                        isFileDownloadLoading ||
                        isFilesLoading ||
                        isMapPlanLoading ||
                        isMapPlanFetching ||
                        isLoadingGridLayout
                    }

                    // -----------------Grid Layout-------
                    dataItems={filteredPlanLibraries || []}
                    reorderable
                    handleSearch={handleInputChange}
                    handleSaveLayout={handleSaveLayout}
                    isSaveLayout={true}
                    gridConfigFromApi={gridConfig}
                    handleResetLayout={handleResetLayout}
                // -----------------Grid Layout-------
                />
            </Grid>
            {(isAddPlan || isDownloadFile) && (
                <Grid item lg={12} sm={12} xl={12} xs={12}>
                    <Grid
                        container
                        direction="row"
                        sx={{
                            justifyContent: "flex-end",
                            alignItems: "flex-start",
                        }}
                    >
                        {/* <GuardedComponent requiredPermissions={[UserPermissionName.DOWNLOAD_FILE]}>
                            {selectedPlanData?.length > 0 && (
                                <Button variant="contained" style={{ width: '130px', marginRight: 5 }} onClick={handleDownloadFiles}>
                                    DOWNLOAD
                                </Button>
                            )}
                        </GuardedComponent> */}
                        {/* <GuardedComponent requiredPermissions={[UserPermissionName.ADD_PLAN]}>
                            <Button variant="contained" style={{ width: '130px' }} onClick={handleCreatePlan}>
                                ADD PLANS
                            </Button>
                        </GuardedComponent> */}
                    </Grid>
                </Grid>
            )}
            {editItem && (
                <UploadPlanDialog
                    data={editItem}
                    mapPlanData={mapPlanData?.data ?? []}
                    config={{
                        saveConfig: editItem?.planID
                            ? planApiKeyStore.updatePlan()
                            : planApiKeyStore.createPlan(),
                        invalidateRoute: () => planReviewApiKeyStore.getPlanReviewTask(planReviewTaskData?.planReviewTaskID),
                    }}
                    planData={updatedPlanData ?? []}
                    yupSchema={planFormSchema}
                    onSavedPlan={handleSavePlan}
                    onClosed={handleEditClose} />
            )}
            {isConfirmDelete && (
                <DeletePlanDialog
                    isParentLoading={isLoading}
                    isDeleteLoading={isDeleteLoading}
                    onDeletePlan={handleDeletePlan}
                    onClosed={() => setConfirmDialog(false)} />
            )}
        </Grid>
    );
}